import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { PaymentRequest } from '../types/customer';
import { paymentsApi } from '../services/api';

interface AdminPaymentState {
  paymentRequests: PaymentRequest[];
  loading: boolean;
  error: string | null;
}

const initialState: AdminPaymentState = {
  paymentRequests: [],
  loading: false,
  error: null,
};

export const fetchAdminPaymentRequests = createAsyncThunk<
  PaymentRequest[],
  void,
  { rejectValue: { message: string } }
>('adminPayments/fetchRequests', async (_, { rejectWithValue }) => {
  try {
    const response = await paymentsApi.getAllPayments();
    return response.payments;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: 'Fetch failed' });
  }
});

export const approvePaymentRequest = createAsyncThunk<
  { paymentId: string; reapStatus?: string; reapError?: string },
  string,
  { rejectValue: { message: string } }
>('adminPayments/approveRequest', async (paymentId, { rejectWithValue }) => {
  try {
    const response = await paymentsApi.approvePayment(paymentId);
    return {
      paymentId,
      reapStatus: response.data.reapStatus,
      reapError: response.data.reapError
    };
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: 'Approve failed' });
  }
});

export const rejectPaymentRequest = createAsyncThunk<
  { paymentId: string; rejectionReason: string },
  { paymentId: string; rejectionReason: string },
  { rejectValue: { message: string } }
>('adminPayments/rejectRequest', async ({ paymentId, rejectionReason }, { rejectWithValue }) => {
  try {
    await paymentsApi.rejectPayment(paymentId, rejectionReason);
    return { paymentId, rejectionReason };
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: 'Reject failed' });
  }
});

export const completePaymentRequest = createAsyncThunk<
  { paymentId: string; completedAt: string },
  string,
  { rejectValue: { message: string } }
>('adminPayments/completeRequest', async (paymentId, { rejectWithValue }) => {
  try {
    const response = await paymentsApi.completePayment(paymentId);
    return {
      paymentId,
      completedAt: response.data.completedAt
    };
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: 'Complete failed' });
  }
});

const adminPaymentSlice = createSlice({
  name: 'adminPayments',
  initialState,
  reducers: {
    clearAdminPaymentError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminPaymentRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminPaymentRequests.fulfilled, (state, action: PayloadAction<PaymentRequest[]>) => {
        state.loading = false;
        state.paymentRequests = action.payload;
      })
      .addCase(fetchAdminPaymentRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to load payment requests';
      })
      .addCase(approvePaymentRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approvePaymentRequest.fulfilled, (state, action: PayloadAction<{ paymentId: string; reapStatus?: string; reapError?: string }>) => {
        state.loading = false;
        const idx = state.paymentRequests.findIndex(req => req._id === action.payload.paymentId);
        if (idx !== -1) {
          state.paymentRequests[idx].status = 'approved';
          // Store Reap API feedback
          (state.paymentRequests[idx] as any).reapStatus = action.payload.reapStatus;
          (state.paymentRequests[idx] as any).reapError = action.payload.reapError;
          state.paymentRequests[idx].reapErrorMessage = action.payload.reapError;
        }
      })
      .addCase(approvePaymentRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to approve payment request';
      })
      .addCase(rejectPaymentRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectPaymentRequest.fulfilled, (state, action: PayloadAction<{ paymentId: string; rejectionReason: string }>) => {
        state.loading = false;
        const idx = state.paymentRequests.findIndex(req => req._id === action.payload.paymentId);
        if (idx !== -1) {
          state.paymentRequests[idx].status = 'rejected';
          (state.paymentRequests[idx] as any).rejectionReason = action.payload.rejectionReason;
        }
      })
      .addCase(rejectPaymentRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to reject payment request';
      })
      .addCase(completePaymentRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completePaymentRequest.fulfilled, (state, action: PayloadAction<{ paymentId: string; completedAt: string }>) => {
        state.loading = false;
        const idx = state.paymentRequests.findIndex(req => req._id === action.payload.paymentId);
        if (idx !== -1) {
          state.paymentRequests[idx].status = 'completed';
        }
      })
      .addCase(completePaymentRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to complete payment request';
      });
  },
});

export const { clearAdminPaymentError } = adminPaymentSlice.actions;
export default adminPaymentSlice.reducer;
